import SubmissionResource from "./SubmissionResource";

/**
 * Skobo Submission class representing a Teams submission from a student as an answer of an assignment
 *
 * @category Models
 * @subcategory Education
 * @class Education
 * @name Submission
 */
class Submission {
  releasedDateTime = null; // DateTime
  returnedBy = null; // Identity object
  returnedDateTime = null; // DateTime
  resourcesFolderUrl = "";
  status = "";
  submittedDateTime = null; // DateTime
  unsubmittedDateTime = null; // DateTime

  outcomes = [];
  resources = [];

  constructor() {}

  // Getters
  /**
   * Gets Graph API payload for creating an assignment
   *
   * @returns {object} Payload object
   */
  get createPayload() {
    const createPayload = {
      displayName: this.displayName, // String
      instructions: this.instructions, // itemBody
      grading: this.grading, // educationAssignmentGradeType,
      allowLateSubmissions: this.allowLateSubmissions, // Boolean
      allowStudentsToAddResourcesToSubmission: this.allowStudentsToAddResourcesToSubmission,
      dueDateTime: this.dueDateTime, // DateTimeOffset UTC
      closeDateTime: this.closeDateTime // DateTimeOffset UTC
    };
    if (this.status === "draft") {
      createPayload.assignDateTime = this.assignDateTime; // DateTimeOffset UTC
    }
    return createPayload;
  }

  get publishedOutcomeFeedback() {
    const feedbackOutcome = this.outcomes.find(o => o["@odata.type"] === "#microsoft.graph.educationFeedbackOutcome");

    if (feedbackOutcome && feedbackOutcome.publishedFeedback && feedbackOutcome.publishedFeedback.text) {
      return feedbackOutcome.publishedFeedback.text.content;
    }
    return null;
  }

  get publishedOutcomeFeedbackAuthorId() {
    const feedbackOutcome = this.outcomes.find(o => o["@odata.type"] === "#microsoft.graph.educationFeedbackOutcome");

    if (
      feedbackOutcome &&
      feedbackOutcome.publishedFeedback &&
      feedbackOutcome.publishedFeedback.feedbackBy &&
      feedbackOutcome.publishedFeedback.feedbackBy.user
    ) {
      return feedbackOutcome.publishedFeedback.feedbackBy.user.id;
    }
    return null;
  }

  get publishedOutcomePoints() {
    const pointsOutcome = this.outcomes.find(o => o["@odata.type"] === "#microsoft.graph.educationPointsOutcome");

    if (pointsOutcome && pointsOutcome.publishedPoints) {
      return pointsOutcome.publishedPoints.points;
    }
    return null;
  }

  /**
   * Expose enum
   *
   * @returns {object} NCT Status Definition
   */
  static get nctStatusEnum() {
    return nctStatusEnumDefinition;
  }

  //
  //
  // Methods

  /**
   * Calculate number of days since dueDateTime
   *
   * @param {Date} dueDateTime Due date time submission should be calculated from
   * @returns {number} Number of days since since dueDateTime
   */
  noOfDaysTooLate(dueDateTime) {
    if (dueDateTime instanceof Date) {
      if (this.submittedDateTime && dueDateTime && this.submittedDateTime > dueDateTime) {
        const differenceInMs = Math.abs(this.submittedDateTime.getTime() - dueDateTime.getTime());
        return Math.round(differenceInMs / 1000 / 3600 / 24);
      }
    }
    return 0;
  }

  /**
   * Calculates NCT status of submission
   *
   * @param { Date } dueDateTime Due date time submission should be calculated from
   * @returns { string } NCT Submission Enum status of submission
   */
  nctStatus(dueDateTime) {
    switch (this.status) {
      case "submitted":
        if (this.noOfDaysTooLate(dueDateTime) <= 0) {
          return Submission.nctStatusEnum.SUBMITTED;
        }
        return Submission.nctStatusEnum.SUBMITTEDLATE;

      case "released":
        return Submission.nctStatusEnum.RELEASED;

      case "returned":
        return Submission.nctStatusEnum.RETURNED;

      case "working":
      default:
        // Something about viewed missing here
        return Submission.nctStatusEnum.WORKING;
    }
  }

  // Static Methods

  /**
   * Parse submission object from Graph API to NCT Submission
   *
   * @param {object} graphSubmission    Object from graph api
   * @returns {object} NCT Submission
   */
  static prepareForSkolebordet(graphSubmission) {
    const submission = Object.assign(new Submission(), graphSubmission);

    if (submission.submittedDateTime && submission.submittedDateTime !== "") {
      submission.submittedDateTime = new Date(submission.submittedDateTime);
    }

    if (submission.unsubmittedDateTime && submission.unsubmittedDateTime !== "") {
      submission.unsubmittedDateTime = new Date(submission.unsubmittedDateTime);
    }

    if (submission.returnedDateTime && submission.returnedDateTime !== "") {
      submission.returnedDateTime = new Date(submission.returnedDateTime);
    }

    if (submission.releasedDateTime && submission.releasedDateTime !== "") {
      submission.releasedDateTime = new Date(submission.releasedDateTime);
    }

    if (submission.resources) {
      submission.resources = submission.resources.map(r => SubmissionResource.parseFromGraph(r));
    }

    if (submission.submittedResources) {
      submission.submittedResources = submission.submittedResources.map(r => SubmissionResource.parseFromGraph(r));
    }

    return submission;
  }

  static generateGraphRequestFromAssignment(assignment) {
    if (assignment && assignment.id && assignment.classId) {
      return {
        id: assignment.id,
        method: "GET",
        url: `/education/classes/${assignment.classId}/assignments/${assignment.id}/submissions?$expand=resources,submittedResources,outcomes`
      };
    }
  }
}

const nctStatusEnumDefinition = {
  VIEWED: "viewed",
  WORKING: "working",
  SUBMITTED: "submitted",
  SUBMITTEDLATE: "submittedlate",
  RELEASED: "released",
  RETURNED: "returned"
};

export default Submission;
